import React, { useState, useEffect } from "react";
import i18n from "i18next";

// Fluent UI Imports
import { useBoolean } from "@fluentui/react-hooks";

import { SchraubfallOptions } from "./SchraubfallOptions";
import AerospaceDialog from "./AerospaceDialog";
import GearboxSelection from "./GearboxSelection";
import GearedOffsetHeadSpecifications from "./GearedOffsetHeadSpecifications";
import Lsp3Selection from "./Lsp3Selection";
import Lsp3Connect from "./Lsp3Connect";
import AngleHeadSpecifications from "./AngleHeadSpecifications";
import OutputSocketGearSpecifications from "./OutputSocketGearSpecifications";

import { fetchWrapper } from "../../helpers/fetchwrapper";
import { helperMethods } from "../../helpers/helperMethods";
import { MmInch } from "../../helpers/mminch";

import { baseUrl } from "../../helpers/constants";
import AngleHeadSelection from "./AngleHeadSelection";
import ToolSelection from "./ToolSelection";
import ServiceSelection from "./ServiceSelection";
import ConfiguratorStResult from "./ConfiguratorStResult";
import { IKonfigurationSt } from "../../Models/IKonfigurationSt";
import useWindowSize from "../../helpers/useWindowSize";
import authService from "../api-authorization/AuthorizeService";
import {
  Button,
  Dialog,
  DialogContent,
  Field,
  Image,
  ImageSwatch,
  ProgressBar,
  SwatchPicker,
  Label,
  Input,
  Option,
  MessageBar,
  Dropdown,
  RadioGroup,
  Radio,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  Subtitle1,
  DialogTrigger,
} from "@fluentui/react-components";
import {
  ArrowClockwiseRegular,
  ArrowLeftRegular,
  ArrowRightRegular,
  SearchRegular,
} from "@fluentui/react-icons";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import { ISchraubprofil } from "../../Models/ISchraubprofil";
import { ILuebOption } from "../../Models/ILuebOption";
import MeasurementInputs, { IMeasurements } from "./MeasurementInputs";

interface IConfiguratorPanelProps {
  isPanelOpen: boolean;
  onDismiss: any;
  config?: IKonfigurationSt;
}

function ConfiguratorStPanel(props: IConfiguratorPanelProps) {
  const [currentPage, setCurrentPage] = useState("schraubfall");
  const [percentComplete, setPercentComplete] = useState(0);

  const [preiseSichtbar, setPreiseSichtbar] = useState(false);

  const [imagesPreload, setImagesPreload] = useState<any>([]);
  const [schraubprofile, setSchraubprofile] = useState<ILuebOption[]>([]);
  const [bolzenprofile, setBolzenprofile] = useState<ILuebOption[]>([]);
  const [configStarted, setConfigStarted] = useState<boolean>(false);

  const [_blankConfig, set_blankConfig] = useState({
    schraubfall: "schraube",
    anzugsmoment: { nm: 0.0, inlbs: 0.0 },
    schluesselweite: { mm: 0.0, inchDez: 0.0, inch: "" },
    fastenerProfile: {
      kurzwort: "",
      key: "",
      text: "",
      data: {},
    },
    anzugsrichtung: "right",
    rueckstellung: "mechanisch",
    gegenhalterSchluesselweite: { mm: 0.0, inchDez: 0.0, inch: "" },
    gegenhalterAussenlaenge: { mm: 0.0, inchDez: 0.0, inch: "" },
    gegenhalterVerschraubungslaenge: { mm: 0.0, inchDez: 0.0, inch: "" },
    retainingProfile: { kurzwort: "", key: "", text: "" },
    gearbox: { key: "", text: "" },
    angleHeadSpecifications: undefined,
    gearedOffsetHeadSpecifications: undefined,
    outputSocketSpecifications: undefined,
    axialAdapter: undefined,
    lsp3Selection: {
      items: {
        selectedLsp3Article: { artikelId: "", kennzahlAntrieb: 0 },
        selectedAntriebsmodul: { artikelId: "", kennzahlAntrieb: 0 },
      },
      selectionComplete: false,
    },
    selectedAntriebsset: {
      artikelId: "",
    },
    selectedAngleHead: {
      nonLuebAngleHead: false,
      withOutAngleHead: false,
      selectedAngleHead: { artikelId: "", kennzahlAntrieb: 0 },
    },
    lsp3Connect: {
      selectedAdapter: undefined,
      selectedZwischenelement: undefined,
      reactionBar: false,
    },
    tool: { tool: { kennzahlAbtrieb: 0 } },
    service: undefined,
  });

  const [
    isAerospaceDialogOpen,
    { setTrue: openAerospaceDialog, setFalse: dismissAerospaceDialog },
  ] = useBoolean(false);

  const [
    isDriveSetSelectedOpen,
    {
      setTrue: openDriveSetSelectedDialog,
      setFalse: dismissDriveSetSelectedDialog,
    },
  ] = useBoolean(false);

  const [
    isAxialAdapterSelectedOpen,
    {
      setTrue: openAxialAdapterSelectedDialog,
      setFalse: dismissAxialAdapterSelectedDialog,
    },
  ] = useBoolean(false);

  const anzugsrichtungOptions: ILuebOption[] = [
    { key: "left", text: i18n.t<string>("left") },
    { key: "right", text: i18n.t<string>("right") },
  ];

  const rueckstellungOptions: ILuebOption[] = [
    { key: "mechanisch", text: i18n.t<string>("mechanisch") },
    { key: "sensorisch", text: i18n.t<string>("sensorisch") },
    { key: "ohne", text: i18n.t<string>("ohne") },
  ];

  const [configuration, setConfiguration] = useState(_blankConfig);
  const handleConfigurationValuesChange = (name: string, data: any) => {
    if (!configStarted && props.isPanelOpen) {
      addStartedConfig();
    }

    setConfiguration((prevState) => ({
      ...prevState,
      [name]: data,
    }));
  };

  const [finishedConfig, setFinishedConfig] = useState<
    IKonfigurationSt | undefined
  >(undefined);

  const handleFinishedConfigValuesChange = (name: string, data: any) => {
    setFinishedConfig((prevState: any) => ({
      ...prevState,
      [name]: data,
    }));
  };

  const [showSaveConfigDialog, setShowSaveConfigDialog] =
    useState<boolean>(false);
  const [showRequestOfferDialog, setShowRequestOfferDialog] =
    useState<boolean>(false);

  const [isVertrieb, setIsVertrieb] = useState<boolean>(false);

  useEffect(() => {
    if (props.config) {
      setFinishedConfig(props.config);
      setCurrentPage("lsp3Result");
      getIsVertrieb();
    }
  }, [props.config]);

  const checkInput = (inputValue: string): boolean => {
    if (
      !Number.isNaN(Number(inputValue)) ||
      inputValue.includes("/") ||
      inputValue.includes(",") ||
      inputValue.includes(" ") ||
      inputValue == ""
    ) {
      return true;
    } else return false;
  };

  function onChangeProfile(data: ISchraubprofil | undefined) {
    if (data != undefined) {
      handleConfigurationValuesChange("fastenerProfile", {
        key: data.schraubprofilGuid,
        kurzwort: data.kurzwort,
        text: i18n.language.includes("de")
          ? data.displayNameDE
          : data.displayNameEN,
        data: data,
      });
    }
  }
  function onChangeRetainingProfile(data: ISchraubprofil | undefined) {
    if (data != undefined) {
      handleConfigurationValuesChange("retainingProfile", {
        key: data.schraubprofilGuid,
        kurzwort: data?.kurzwort,
        text: i18n.language.includes("de")
          ? data.displayNameDE
          : data.displayNameEN,
      });
    }
  }

  const getPreiseSichtbar = () => {
    authService.getUser().then((user: any) => {
      if (user) {
        fetchWrapper
          .getWithAuth(`${baseUrl}/api/account/getcurrentuser`)
          .then((result) => {
            setPreiseSichtbar(result.preiseSichtbar);
          });
      }
    });
  };

  const getIsVertrieb = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/getmyroles`)
      .then((result: string[]) => {
        if (result.includes("Vertrieb")) {
          setIsVertrieb(true);
        }
      })
      .catch((error) => {});
  };

  const finishConfig = () => {
    if (finishedConfig && isVertrieb) {
      fetchWrapper
        .postWithAuth(`${baseUrl}/api/vertrieb/FinishConfig`, finishedConfig)
        .then((result) => {
          props.onDismiss();
        });
    }
  };

  const getAxialAdapter = () => {
    const kan = configuration?.tool?.tool?.kennzahlAbtrieb; // KAN ist die KAB vom Tool
    let kab = 0;

    // KAB ist die KAN vom Winkelkopf, hier prüfen ob Winkelkopf stand alone oder zusätzlich
    if (configuration.gearbox.key == "Winkelkopf") {
      kab =
        configuration?.lsp3Selection?.items?.selectedLsp3Article
          .kennzahlAntrieb;
    } else if (
      configuration?.selectedAngleHead?.selectedAngleHead?.artikelId != ""
    ) {
      kab =
        configuration?.selectedAngleHead?.selectedAngleHead?.kennzahlAntrieb;
    } else if (
      configuration?.lsp3Selection?.items?.selectedAntriebsmodul?.artikelId !=
      ""
    ) {
      // Wenn wir ein Antriebsmodul haben, haben wir sicher einen Inline Adapter, daher benötigen wir für den Axial Adapter die KAN vom Modul
      kab =
        configuration?.lsp3Selection?.items?.selectedAntriebsmodul
          ?.kennzahlAntrieb;
    }

    if (kan && kan > 0 && kab > 0) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/configurator/GetAxialAdapter?kan=${kan}&kab=${kab}`,
        )
        .then((data: any) => {
          if (data == "") {
            handleConfigurationValuesChange("axialAdapter", undefined);
            handleConfigurationValuesChange("axialAdapterNotFound", true);
          } else {
            handleConfigurationValuesChange("axialAdapter", data);
            handleConfigurationValuesChange("axialAdapterNotFound", false);
          }
        });
    }
  };

  const addStartedConfig = () => {
    setConfigStarted(true);
    // KPI Wert für Konfiguration gestartet setzen
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/configurator/AddStartedConfig`)
      .then((data: any) => {});
  };

  const getAntriebsprofile = (isSchraube: string) => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/configurator/GetSchraubprofile?isSchraube=${isSchraube}`,
      )
      .then((data: ISchraubprofil[]) => {
        let options: ILuebOption[] = [];
        let images: string[] = imagesPreload;

        let currentProfileInList = false;

        data.forEach((element: any) => {
          if (configuration.fastenerProfile.key == element.schraubprofilGuid) {
            currentProfileInList = true;
          }

          let option: ILuebOption = {
            data: element,
            key: element.schraubprofilGuid,
            text: i18n.language.includes("de")
              ? element.displayNameDE
              : element.displayNameEN,
          };
          options.push(option);

          // Workaround: Bilder cachen
          const keyword = isSchraube ? "Schraube" : "Mutter";
          const imageUrl = `${baseUrl}/IMAGES/PROFILBILDER/${keyword}_${element.schraubprofilGuid}.JPG`;
          if (!images.some((x: string) => x == imageUrl)) {
            images.push(imageUrl);
          }
        });

        setImagesPreload(images);
        setSchraubprofile(options);
        if (options.length > 0 && !currentProfileInList) {
          // Wenn aktuell ausgewähltes Profil NICHT in Liste, den ersten Eintrag setzen
          handleConfigurationValuesChange("fastenerProfile", {
            key: options[1].key.toString(),
            kurzwort: options[1].data.kurzwort.toString(),
            text: options[1].text.toString(),
            data: options[1].data,
          });
        }
      });
  };

  const configRestarted = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/configurator/ConfigRestarted`)
      .then((data: any) => {});
  };

  const getBolzenprofile = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/configurator/GetBolzenprofile`)
      .then((data: any) => {
        let options: ILuebOption[] = [];
        let images: string[] = imagesPreload;
        let currentProfileInList = false;

        data.forEach((element: any) => {
          if (configuration.retainingProfile.key == element.schraubprofilGuid) {
            currentProfileInList = true;
          }

          let option: ILuebOption = {
            data: element,
            key: element.schraubprofilGuid,
            text: i18n.language.includes("de")
              ? element.displayNameDE
              : element.displayNameEN,
          };
          options.push(option);

          // Workaround: Bilder cachen
          const imageUrl = `${baseUrl}/IMAGES/PROFILBILDER/BOLZEN_${element.schraubprofilGuid}.JPG`;
          if (!images.some((x: string) => x == imageUrl)) {
            images.push(imageUrl);
          }
        });

        setImagesPreload(images);
        setBolzenprofile(options);

        if (options.length > 0 && !currentProfileInList) {
          handleConfigurationValuesChange("retainingProfile", {
            key: options[0].key.toString(),
            kurzwort: options[0].data.kurzwort.toString(),
            text: options[0].text.toString(),
            data: options[0].data,
          });
        }
      });
  };

  const nmConverter = () => {
    const nm = helperMethods.convertToNumber(configuration.anzugsmoment.nm);
    handleConfigurationValuesChange("anzugsmoment", {
      nm: nm.toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      inlbs: (nm * 8.8507457676).toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
    });
  };

  const lbsInConverter = () => {
    const inlbs = helperMethods.convertToNumber(
      configuration.anzugsmoment.inlbs,
    );
    handleConfigurationValuesChange("anzugsmoment", {
      inlbs: inlbs.toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      nm: (inlbs * 0.11298482933333).toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
    });
  };

  const getValFromArray = (array: any, field: string) => {
    let test = undefined;
    for (var i = 0, iLen = Object.entries(array).length; i < iLen; i++) {
      // do stuff with node.childNodes[i];
      if (Object.entries(array)[i][0] == field) {
        test = Object.entries(array)[i][1];
      }
    }
    return test;
  };

  const inchConverter = (field: string) => {
    const fieldValue = getValFromArray(configuration, field);
    const inchDezValue = getValFromArray(fieldValue, "inchDez");
    const asNumber = helperMethods.convertToNumber(inchDezValue);
    const mm = helperMethods.convertToNumber(MmInch.toMM(asNumber));
    handleConfigurationValuesChange(field, {
      mm: mm.toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      inchDez: asNumber.toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      inch: asNumber == 0 ? "" : MmInch.toInch(mm),
    });
  };

  const fractionalConverter = (field: string) => {
    const fieldValue = getValFromArray(configuration, field);
    const inchValue = getValFromArray(fieldValue, "inch");
    var fixedFraction = (inchValue as string)
      .replace(",", "")
      .replace(/\s*\/\s*/g, "/");

    const mm = helperMethods.convertToNumber(MmInch.toMM(fixedFraction));
    handleConfigurationValuesChange(field, {
      mm: mm.toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      inchDez: (mm / 25.4).toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      inch: fixedFraction,
    });
  };

  const mmConverter = (field: string) => {
    const fieldValue = getValFromArray(configuration, field);
    const mmValue = getValFromArray(fieldValue, "mm");
    const mm = helperMethods.convertToNumber(mmValue as string);
    handleConfigurationValuesChange(field, {
      mm: mm.toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      inchDez: (mm / 25.4).toLocaleString(i18n.language, {
        maximumFractionDigits: 3,
      }),
      inch: mm ? MmInch.toInch(mm) : "",
    });
  };

  const onSelectNorm = (norm: any) => {
    if (norm?.selectedCollar?.norm) {
      handleConfigurationValuesChange(
        "selectedCollarNorm",
        norm?.selectedCollar?.norm + "-" + norm?.selectedCollar?.code,
      );
    } else {
      handleConfigurationValuesChange("selectedCollarNorm", undefined);
    }
    if (norm?.selectedBolzen?.norm) {
      handleConfigurationValuesChange(
        "selectedBolzenNorm",
        norm?.selectedBolzen?.norm + "-" + norm?.selectedBolzen?.code,
      );
    } else {
      handleConfigurationValuesChange("selectedBolzenNorm", undefined);
    }

    if (norm?.selectedCollar?.geometrie_Collar) {
      var sprofil = schraubprofile.filter(
        (x) => x.data?.kurzwort == norm?.selectedCollar?.geometrie_Collar,
      );
      if (sprofil != undefined) {
        // In der Liste dürfte nur noch 1 Eintrag sein!
        if (sprofil.length == 1) {
          var selectedProfile = sprofil[0];
          handleConfigurationValuesChange("fastenerProfile", {
            key: selectedProfile ? selectedProfile.key.toString() : "",
            kurzwort: selectedProfile
              ? selectedProfile.data.kurzwort.toString()
              : "",
            text: selectedProfile ? selectedProfile.text.toString() : "",
            data: selectedProfile ? selectedProfile.data : {},
          });
        }
      }
    }

    handleConfigurationValuesChange("gegenhalterSchluesselweite", {
      mm: helperMethods.nachkomma(norm.selectedBolzen.schluesselweite, 2),
      inchDez: helperMethods.nachkomma(
        norm.selectedBolzen.schluesselweite / 25.4,
        3,
      ),
      inch: MmInch.toInch(
        helperMethods.convertToNumber(norm.selectedBolzen.schluesselweite),
      ),
    });
    handleConfigurationValuesChange("schluesselweite", {
      mm: helperMethods.nachkomma(norm.selectedCollar.schluesselweite, 2),
      inchDez: helperMethods.nachkomma(
        norm.selectedCollar.schluesselweite / 25.4,
        3,
      ),
      inch: MmInch.toInch(
        helperMethods.convertToNumber(norm.selectedCollar.schluesselweite),
      ),
    });
    handleConfigurationValuesChange("anzugsmoment", {
      nm: norm.selectedCollar.abreiss_Drehmoment,
      inlbs: (
        norm.selectedCollar.abreiss_Drehmoment * 8.8507457676
      ).toLocaleString(undefined, { maximumFractionDigits: 3 }),
    });
    var profil = schraubprofile.find(
      (x: any) => x.displayNameDE == norm.selectedCollar.geometrie_Collar,
    );
    dismissAerospaceDialog();
  };

  const isModelDisabled = () => {
    switch (currentPage) {
      case "schraubfall":
        if (
          configuration.anzugsmoment.nm == 0.0 ||
          Number.isNaN(configuration.anzugsmoment.nm)
        ) {
          return true;
        }
        break;
      case "model":
        if (
          configuration.anzugsmoment.nm == 0.0 ||
          Number.isNaN(configuration.anzugsmoment.nm)
        ) {
          return true;
        }
        break;
      case "headSpecifications":
        if (
          configuration.angleHeadSpecifications == undefined &&
          configuration.gearedOffsetHeadSpecifications == undefined
        ) {
          return true;
        }
        break;
      case "lsp3touch":
        if (
          ![
            "Standard_FL_G",
            "Inline_FL_G",
            "Standard_FL_O",
            "Inline_FL_O",
          ].includes(configuration.gearbox.key) &&
          configuration.angleHeadSpecifications == undefined
        ) {
          return true;
        }
        break;
      case "lsp3Selection":
        if (!configuration.lsp3Selection.selectionComplete) {
          return true;
        }
        break;
      case "angleHeadSelection":
        if (
          !configuration.selectedAngleHead.withOutAngleHead &&
          !configuration.selectedAngleHead.nonLuebAngleHead &&
          configuration.selectedAngleHead.selectedAngleHead == undefined
        ) {
          return true;
        }
        break;
      case "toolSelection":
        // Auskommentiert, Man soll ohne Tool fortfahren können
        // if (!configuration.tool) {
        //   return true;
        // }
        break;
      default:
        return false;
    }
    return false;
  };

  useEffect(() => {
    if (currentPage == "schraubfall" && props.isPanelOpen) {
      getPreiseSichtbar();
      getAntriebsprofile(configuration.schraubfall == "schraube" ? "1" : "0");
      getBolzenprofile();
    }
  }, [currentPage, props.isPanelOpen]);

  const windowSize = useWindowSize();

  const schraubfallOptions = SchraubfallOptions();

  useEffect(() => {
    if (props.isPanelOpen == false && finishedConfig != undefined) {
      if (
        !showRequestOfferDialog &&
        !showSaveConfigDialog &&
        !isDriveSetSelectedOpen &&
        !isAerospaceDialogOpen &&
        !isAxialAdapterSelectedOpen
      ) {
        props.onDismiss();

        // configStarted zurücksetzen damit eine neue Config wieder über die Statistik erfasst wird.
        setConfigStarted(false);

        // Zurücksetzen der Konfig mit Timeout damit der Rendering Effekt schöner ist!
        setTimeout(() => {
          setPercentComplete(0);
          setCurrentPage("schraubfall");
          setConfiguration(_blankConfig);
          setFinishedConfig(undefined);
        }, 500);
      }
    }
  }, [props.isPanelOpen]);
  return (
    <div>
      {props.isPanelOpen && (
        <>
          <>
            <AerospaceDialog
              schraubfall={configuration.schraubfall}
              isDialogOpen={isAerospaceDialogOpen}
              onDismiss={() => dismissAerospaceDialog()}
              onSelectNorm={onSelectNorm}
            />

            <Dialog open={isAxialAdapterSelectedOpen}>
              <DialogSurface>
                <DialogBody>
                  <DialogTitle>
                    {i18n.t<string>("axialAdapterSelectedTitle")}
                  </DialogTitle>
                  <DialogContent>
                    <Field>
                      {i18n.t<string>("axialAdapterSelectedSubText")}
                    </Field>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => {
                        dismissAxialAdapterSelectedDialog();
                        setCurrentPage("serviceSelection");
                      }}
                      appearance="primary"
                    >
                      {i18n.t<string>("next")}
                    </Button>
                    <Button
                      onClick={() => {
                        dismissAxialAdapterSelectedDialog();
                      }}
                    >
                      {i18n.t<string>("cancel")}
                    </Button>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>
            <Dialog open={isDriveSetSelectedOpen}>
              <DialogSurface>
                <DialogBody>
                  <DialogTitle>
                    {["Inline_FL_G", "Inline_FL_O"].includes(
                      configuration.gearbox.key,
                    )
                      ? i18n.t<string>("driveModuleSelectedTitle")
                      : i18n.t<string>("driveSetSelectedTitle")}
                  </DialogTitle>
                  <DialogContent>
                    <Field>
                      {["Inline_FL_G", "Inline_FL_O"].includes(
                        configuration.gearbox.key,
                      ) && i18n.t<string>("driveModuleSelectedSubText")}
                      {!["Inline_FL_G", "Inline_FL_O"].includes(
                        configuration.gearbox.key,
                      ) && i18n.t<string>("driveSetSelectedSubText")}
                    </Field>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      appearance="primary"
                      onClick={() => {
                        dismissDriveSetSelectedDialog();
                        if (
                          ["Standard_FL_G", "Standard_FL_O"].includes(
                            configuration.gearbox.key,
                          )
                        ) {
                          // Wenn LSP3 Standard, Connect Pivot anzeigen
                          setCurrentPage("lsp3Connect");
                        } else {
                          // Sonst direkt zur Toolauswahl
                          setCurrentPage("toolSelection");
                        }
                      }}
                    >
                      {i18n.t<string>("next")}
                    </Button>
                    <Button
                      onClick={() => {
                        dismissDriveSetSelectedDialog();
                      }}
                    >
                      {i18n.t<string>("cancel")}
                    </Button>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>
          </>

          {(!finishedConfig || finishedConfig.status < 10) && (
            <div className="lueb-InnerContentBlockPadding2px">
              <Dialog>
                <DialogTrigger action="open">
                  <Button
                    appearance="transparent"
                    icon={<ArrowClockwiseRegular />}
                  >
                    {i18n.t<string>("reset")}
                  </Button>
                </DialogTrigger>
                <DialogSurface>
                  <DialogBody>
                    <DialogTitle>{i18n.t<string>("resetConfig")}</DialogTitle>
                    <DialogContent>
                      {i18n.t<string>("resetConfigText")}
                    </DialogContent>
                    <DialogActions>
                      <DialogTrigger action="close">
                        <Button
                          appearance="primary"
                          icon={<ArrowClockwiseRegular />}
                          onClick={() => {
                            setTimeout(() => {
                              setPercentComplete(0);
                              setConfiguration(_blankConfig);
                              setFinishedConfig(undefined);
                              setTimeout(() => {
                                setCurrentPage("schraubfall");
                              }, 100);
                            }, 500);
                          }}
                        >
                          {i18n.t<string>("reset")}
                        </Button>
                      </DialogTrigger>
                      <DialogTrigger action="close">
                        <Button>{i18n.t<string>("cancel")}</Button>
                      </DialogTrigger>
                    </DialogActions>
                  </DialogBody>
                </DialogSurface>
              </Dialog>
              {currentPage == "schraubfall" && (
                <Button
                  appearance="transparent"
                  icon={<SearchRegular />}
                  onClick={openAerospaceDialog}
                  disabled={
                    configuration.schraubfall != "mutter" &&
                    configuration.schraubfall != "holdndrive"
                  }
                >
                  {i18n.t<string>("datenAusAeroSpaceNorm")}
                </Button>
              )}
            </div>
          )}
          <div className="lueb-ContentBlock" style={{ padding: "12px" }}>
            <div>
              {finishedConfig?.userObjectGuid == undefined && (
                <Field
                  label={i18n.t<string>(currentPage)}
                  validationState="none"
                  validationMessage={
                    percentComplete * 100 + i18n.t<string>("percentComplete")
                  }
                  style={{ marginBottom: "10px" }}
                >
                  <ProgressBar thickness="large" value={percentComplete} />
                </Field>
              )}
            </div>
            <div>
              {finishedConfig?.userObjectGuid != undefined && (
                <Field
                  label={
                    i18n.t<string>("configst") +
                    ": " +
                    finishedConfig.id +
                    " / " +
                    finishedConfig.kommentar
                  }
                  validationState="none"
                  validationMessage={i18n.t<string>(
                    "configStStatus" + finishedConfig.status,
                  )}
                >
                  <ProgressBar
                    thickness="large"
                    value={
                      finishedConfig ? finishedConfig.status / 100 : undefined
                    }
                  />
                </Field>
              )}
            </div>
          </div>
          {currentPage != "lsp3Selection" && currentPage != "lsp3Result" && (
            <>
              {currentPage == "angleHeadSelection" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  {i18n.t<string>("angleHeadSelection")}
                </Subtitle1>
              )}
              {currentPage == "lsp3touch" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  {i18n.t<string>("outputSocketGearSpecifications")}
                </Subtitle1>
              )}
              {currentPage == "headSpecifications" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  {i18n.t<string>("angabenFlachabtrieb")}
                </Subtitle1>
              )}
              {currentPage == "model" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  {i18n.t<string>("selectGearbox")}
                </Subtitle1>
              )}
              {currentPage == "schraubfall" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  {i18n.t<string>("selectSchraubfall")}
                </Subtitle1>
              )}
              {currentPage == "lsp3Connect" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  L.SP3 Connect
                </Subtitle1>
              )}
              {currentPage == "toolSelection" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  {i18n.t<string>("toolSelection")}
                </Subtitle1>
              )}
              {currentPage == "serviceSelection" && (
                <Subtitle1 style={{ marginLeft: "10px" }}>
                  {i18n.t<string>("serviceSelection")}
                </Subtitle1>
              )}
              <div className="lueb-ContentBlock">
                {currentPage == "schraubfall" && (
                  <div>
                    <SwatchPicker
                      selectedValue={configuration.schraubfall}
                      defaultSelectedValue={configuration.schraubfall}
                      onSelectionChange={(ev: any, data: any) => {
                        handleConfigurationValuesChange(
                          "schraubfall",
                          data.selectedValue,
                        );
                        handleConfigurationValuesChange(
                          "AbtriebsprofilGuid",
                          "",
                        );
                        handleConfigurationValuesChange(
                          "fastenerProfilKurzwort",
                          "",
                        );
                        getAntriebsprofile(data.key == "schraube" ? "1" : "0");
                      }}
                    >
                      <div style={{ display: "flex", flexFlow: "wrap" }}>
                        {schraubfallOptions.map((item: ILuebOption) => {
                          return (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "2px",
                              }}
                            >
                              <ImageSwatch
                                style={{
                                  width: 120,
                                  height: 120,
                                }}
                                src={item.icon ? item.icon : ""}
                                value={
                                  typeof item.key == "string" ? item.key : ""
                                }
                              />
                              <Label
                                style={{ marginTop: "5px" }}
                                weight="semibold"
                              >
                                {item.text}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    </SwatchPicker>
                    <Label style={{ marginTop: "10px" }}>
                      {i18n.t<string>("anzugsmoment")}
                    </Label>
                    <StackShim
                      horizontal
                      tokens={{ childrenGap: 5 }}
                      wrap={true}
                    >
                      <StackItemShim
                        grow={1}
                        style={{ paddingLeft: 5, paddingRight: 5 }}
                      >
                        <Field>
                          <Input
                            onFocus={(event) => {
                              event.target.select();
                            }}
                            value={configuration.anzugsmoment.nm.toLocaleString(
                              undefined,
                              { maximumFractionDigits: 3 },
                            )}
                            onBlur={nmConverter}
                            autoComplete="off"
                            tabIndex={1}
                            contentBefore="Nm"
                            onKeyDown={(e: any) => {
                              if (e.key == "Enter") {
                                nmConverter();
                              }
                            }}
                            onChange={(ev, data) => {
                              if (checkInput(data.value)) {
                                handleConfigurationValuesChange(
                                  "anzugsmoment",
                                  {
                                    nm: data.value,
                                    inlbs: configuration.anzugsmoment.inlbs,
                                  },
                                );
                              }
                            }}
                          />
                        </Field>
                      </StackItemShim>
                      <StackItemShim
                        grow={1}
                        style={{ paddingLeft: 5, paddingRight: 5 }}
                      >
                        <Field>
                          <Input
                            onFocus={(event) => {
                              event.target.select();
                            }}
                            autoComplete="off"
                            onBlur={lbsInConverter}
                            onKeyDown={(e: any) => {
                              if (e.key == "Enter") {
                                lbsInConverter();
                              }
                            }}
                            onChange={(ev, data) => {
                              if (checkInput(data.value)) {
                                handleConfigurationValuesChange(
                                  "anzugsmoment",
                                  {
                                    inlbs: data.value,
                                    nm: configuration.anzugsmoment.nm,
                                  },
                                );
                              }
                            }}
                            value={configuration.anzugsmoment.inlbs.toLocaleString(
                              undefined,
                              { maximumFractionDigits: 3 },
                            )}
                            contentBefore={"in.lbs"}
                          />
                        </Field>
                      </StackItemShim>
                    </StackShim>

                    {(!configuration.anzugsmoment.nm ||
                      configuration.anzugsmoment.nm == 0.0) && (
                      <MessageBar style={{ marginTop: "5px" }} intent="error">
                        {i18n.t<string>("errorTighteningTorque")}
                      </MessageBar>
                    )}

                    <MeasurementInputs
                      title={"schluesselweite"}
                      tabIndex={2}
                      currentMeasurements={configuration.schluesselweite}
                      onChangeMeasurements={(measurements: IMeasurements) => {
                        handleConfigurationValuesChange(
                          "schluesselweite",
                          measurements,
                        );
                      }}
                    />
                    <Label style={{ marginTop: "10px" }}>
                      {i18n.t<string>("profil")}
                    </Label>
                    <div style={{ width: "35vw" }}>
                      <StackShim
                        horizontal
                        tokens={{ childrenGap: 10 }}
                        wrap={true}
                      >
                        <StackItemShim
                          grow={1}
                          style={{ paddingLeft: 5, paddingRight: 5 }}
                        >
                          <Field style={{ maxWidth: "80%" }}>
                            {schraubprofile && (
                              <Dropdown
                                tabIndex={3}
                                value={
                                  typeof configuration.fastenerProfile.text ==
                                  "string"
                                    ? configuration.fastenerProfile.text
                                    : ""
                                }
                                selectedOptions={[
                                  typeof configuration.fastenerProfile.key ==
                                  "string"
                                    ? configuration.fastenerProfile.key
                                    : "",
                                ]}
                                onOptionSelect={(ev, data) => {
                                  var schraubprofil = schraubprofile.find(
                                    (x) => x.key == data.optionValue,
                                  )?.data;
                                  if (schraubprofil != undefined) {
                                    onChangeProfile(schraubprofil);
                                  }
                                }}
                              >
                                {schraubprofile.map((item) => {
                                  if (typeof item.key == "string") {
                                    return (
                                      <Option value={item.key}>
                                        {item.text}
                                      </Option>
                                    );
                                  }
                                })}
                              </Dropdown>
                            )}
                          </Field>
                        </StackItemShim>
                        <StackItemShim
                          grow={1}
                          style={{ paddingLeft: 5, paddingRight: 5 }}
                        >
                          {configuration.fastenerProfile.kurzwort && (
                            <Image
                              height="80px"
                              src={`${baseUrl}/IMAGES/PROFILBILDER/${
                                configuration.schraubfall == "schraube"
                                  ? "SCHRAUBE"
                                  : "MUTTER"
                              }_${configuration.fastenerProfile.kurzwort}.JPG`}
                            />
                          )}
                        </StackItemShim>
                      </StackShim>
                    </div>

                    {configuration.schraubfall == "rohrleitung" && (
                      <>
                        <Subtitle1 style={{ marginTop: "15px" }}>
                          {i18n.t<string>("angabenRohrleitungsverschraubung")}
                        </Subtitle1>
                        <StackShim tokens={{ childrenGap: 10 }} wrap={true}>
                          <StackItemShim
                            grow={1}
                            style={{
                              paddingLeft: 5,
                              paddingRight: 5,
                            }}
                          >
                            <Field
                              style={{ maxWidth: 250 }}
                              label={i18n.t<string>("rueckstellung")}
                            >
                              <Dropdown
                                selectedOptions={[configuration.rueckstellung]}
                                value={
                                  rueckstellungOptions.find(
                                    (x) => x.key == configuration.rueckstellung,
                                  )?.text
                                }
                                onOptionSelect={(ev, data) => {
                                  handleConfigurationValuesChange(
                                    "rueckstellung",
                                    data.optionValue,
                                  );
                                }}
                              >
                                {rueckstellungOptions.map((item) => {
                                  if (typeof item.key == "string") {
                                    return (
                                      <Option value={item.key}>
                                        {item.text}
                                      </Option>
                                    );
                                  }
                                })}
                              </Dropdown>
                            </Field>
                          </StackItemShim>
                          <StackItemShim
                            grow={1}
                            style={{ paddingLeft: 5, paddingRight: 5 }}
                          >
                            <Field label={i18n.t<string>("anzugsrichtung")}>
                              <RadioGroup
                                defaultValue={configuration.anzugsrichtung}
                              >
                                {anzugsrichtungOptions.map((item) => {
                                  return (
                                    <Radio
                                      value={
                                        typeof item.key == "string"
                                          ? item.key
                                          : ""
                                      }
                                      label={item.text}
                                      onChange={(ev, data) => {
                                        handleConfigurationValuesChange(
                                          "anzugsrichtung",
                                          data.value,
                                        );
                                      }}
                                    />
                                  );
                                })}
                              </RadioGroup>
                            </Field>
                          </StackItemShim>
                        </StackShim>
                      </>
                    )}
                    {configuration.schraubfall == "holdndrive" && (
                      <>
                        <Subtitle1 style={{ marginTop: "25px" }}>
                          {i18n.t<string>("angabenGegenhalter")}
                        </Subtitle1>
                        <div>
                          <MeasurementInputs
                            title={"schluesselweite"}
                            tabIndex={1}
                            currentMeasurements={
                              configuration.gegenhalterSchluesselweite
                            }
                            onChangeMeasurements={(
                              measurements: IMeasurements,
                            ) => {
                              handleConfigurationValuesChange(
                                "gegenhalterSchluesselweite",
                                measurements,
                              );
                            }}
                          />
                          <MeasurementInputs
                            title={"aussenlaenge"}
                            tabIndex={2}
                            currentMeasurements={
                              configuration.gegenhalterAussenlaenge
                            }
                            onChangeMeasurements={(
                              measurements: IMeasurements,
                            ) => {
                              handleConfigurationValuesChange(
                                "gegenhalterAussenlaenge",
                                measurements,
                              );
                            }}
                          />

                          <MeasurementInputs
                            title={"verschraubungslaenge"}
                            tabIndex={2}
                            currentMeasurements={
                              configuration.gegenhalterVerschraubungslaenge
                            }
                            onChangeMeasurements={(
                              measurements: IMeasurements,
                            ) => {
                              handleConfigurationValuesChange(
                                "gegenhalterVerschraubungslaenge",
                                measurements,
                              );
                            }}
                          />
                          <Label style={{ marginTop: "10px" }}>
                            {i18n.t<string>("auswahlHalteprofil")}
                          </Label>
                          <div style={{ width: "35vw" }}>
                            <StackShim
                              horizontal
                              tokens={{ childrenGap: 10 }}
                              wrap={true}
                            >
                              <StackItemShim
                                grow={1}
                                style={{ paddingLeft: 5, paddingRight: 5 }}
                              >
                                <Field style={{ maxWidth: "80%" }}>
                                  <Dropdown
                                    onOptionSelect={(ev, data) => {
                                      var bolzenprofil = bolzenprofile.find(
                                        (x) => x.key == data.optionValue,
                                      )?.data;
                                      if (bolzenprofil != undefined) {
                                        onChangeRetainingProfile(bolzenprofil);
                                      }
                                    }}
                                    selectedOptions={[
                                      configuration.retainingProfile.key,
                                    ]}
                                    value={
                                      bolzenprofile.find(
                                        (x) =>
                                          x.key ==
                                          configuration.retainingProfile.key,
                                      )?.text
                                    }
                                  >
                                    {bolzenprofile.map((item) => {
                                      if (typeof item.key == "string")
                                        return (
                                          <Option value={item.key}>
                                            {item.text}
                                          </Option>
                                        );
                                    })}
                                  </Dropdown>
                                </Field>
                              </StackItemShim>
                              <StackItemShim
                                grow={1}
                                style={{ paddingLeft: 5, paddingRight: 5 }}
                              >
                                {configuration.retainingProfile.kurzwort && (
                                  <Image
                                    height="80px"
                                    //Bild zu Vielzahn female fehlt
                                    src={`${baseUrl}/IMAGES/PROFILBILDER/BOLZEN_${configuration.retainingProfile.kurzwort}.JPG`}
                                  />
                                )}
                              </StackItemShim>
                            </StackShim>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {currentPage == "model" && (
                  <GearboxSelection
                    selectedGearboxKey={configuration.gearbox.key}
                    schraubfall={configuration.schraubfall}
                    onGearboxSelectionChanged={(gearbox: string) => {
                      handleConfigurationValuesChange("gearbox", gearbox);
                    }}
                  />
                )}
                {currentPage == "headSpecifications" && (
                  <div>
                    {[
                      "Standard_FL_G",
                      "Inline_FL_G",
                      "Standard_FL_O",
                      "Inline_FL_O",
                    ].includes(configuration.gearbox.key) && (
                      <GearedOffsetHeadSpecifications
                        configuration={
                          configuration.gearedOffsetHeadSpecifications
                        }
                        gearbox={configuration.gearbox}
                        onChangeGearedOffsetHeadSpecifications={(
                          angleHeadSpecs: any,
                        ) => {
                          handleConfigurationValuesChange(
                            "gearedOffsetHeadSpecifications",
                            angleHeadSpecs,
                          );
                        }}
                        schraubfall={configuration.schraubfall}
                        antriebsProfil={configuration.fastenerProfile}
                      />
                    )}
                    {configuration.gearbox.key == "Winkelkopf" && (
                      <AngleHeadSpecifications
                        configuration={configuration.angleHeadSpecifications}
                        onChangeAngleHeadSpecifications={(
                          angleHeadSpecs: any,
                        ) => {
                          handleConfigurationValuesChange(
                            "angleHeadSpecifications",
                            angleHeadSpecs,
                          );
                        }}
                        schraubfall={configuration.schraubfall}
                        antriebsProfil={configuration.fastenerProfile}
                      />
                    )}
                  </div>
                )}
                {[
                  "Standard_FL_G",
                  "Inline_FL_G",
                  "Standard_FL_O",
                  "Inline_FL_O",
                ].includes(configuration.gearbox.key) &&
                  currentPage == "lsp3touch" && (
                    <OutputSocketGearSpecifications
                      configuration={configuration.outputSocketSpecifications}
                      gearbox={configuration.gearbox}
                      schraubfall={configuration.schraubfall}
                      gearedOffsetHeadSpecifications={
                        configuration.gearedOffsetHeadSpecifications
                      }
                      antriebsProfil={configuration.fastenerProfile}
                      onChangeOutputSocketSpecifications={(
                        outputSocket: any,
                      ) => {
                        handleConfigurationValuesChange(
                          "outputSocketSpecifications",
                          outputSocket,
                        );
                      }}
                    />
                  )}
                {currentPage == "lsp3Connect" && (
                  <Lsp3Connect
                    configurationParameter={configuration}
                    onSelectionChanged={(data: any) =>
                      handleConfigurationValuesChange("lsp3Connect", data)
                    }
                  />
                )}
                {currentPage == "angleHeadSelection" && (
                  <AngleHeadSelection
                    preiseSichtbar={preiseSichtbar}
                    configurationParameter={configuration}
                    onSelectionChanged={(data: any) => {
                      handleConfigurationValuesChange(
                        "selectedAngleHead",
                        data,
                      );
                    }}
                    onSelectedAntriebsset={(antriebsset: any) => {
                      handleConfigurationValuesChange(
                        "selectedAntriebsset",
                        antriebsset,
                      );
                    }}
                  />
                )}
                {currentPage == "toolSelection" && (
                  <ToolSelection
                    configurationParameter={configuration}
                    onSelectionChanged={(data: any) =>
                      handleConfigurationValuesChange("tool", data)
                    }
                    preiseSichtbar={preiseSichtbar}
                  />
                )}
                {currentPage == "serviceSelection" && (
                  <ServiceSelection
                    configurationParameter={configuration}
                    onSelectionChanged={(data: any) => {
                      handleConfigurationValuesChange("service", data);
                    }}
                  />
                )}

                <div
                  className={
                    currentPage == "schraubfall" ||
                    (currentPage == "lsp3Result" &&
                      finishedConfig?.userObjectGuid)
                      ? "lueb-flexEnd"
                      : "lueb-spaceBetween"
                  }
                >
                  {currentPage != "schraubfall" &&
                    currentPage != "lsp3Result" &&
                    !finishedConfig?.userObjectGuid && (
                      <Button
                        style={{ marginTop: "5px" }}
                        icon={<ArrowLeftRegular />}
                        onClick={() => {
                          switch (currentPage) {
                            case "model":
                              setPercentComplete(0);
                              setCurrentPage("schraubfall");
                              break;
                            case "headSpecifications":
                              setPercentComplete(0.2);
                              setCurrentPage("model");
                              break;
                            case "lsp3Selection":
                              if (
                                [
                                  "Standard_FL_G",
                                  "Inline_FL_G",
                                  "Standard_FL_O",
                                  "Inline_FL_O",
                                ].includes(configuration.gearbox.key)
                              ) {
                                setCurrentPage("lsp3touch");
                                setPercentComplete(0.4);
                              } else if (
                                configuration.gearbox.key == "Axial_Abtrieb"
                              ) {
                                setCurrentPage("model");
                                setPercentComplete(0.2);
                              } else if (
                                configuration.gearbox.key == "Winkelkopf"
                              ) {
                                setCurrentPage("headSpecifications");
                                setPercentComplete(0.3);
                              }
                              break;
                            case "lsp3touch":
                              setCurrentPage("headSpecifications");
                              setPercentComplete(0.3);
                              break;
                            case "lsp3Connect":
                              setPercentComplete(0.5);
                              setCurrentPage("lsp3Selection");
                              break;
                            case "angleHeadSelection":
                              setPercentComplete(0.6);
                              setCurrentPage("lsp3Connect");
                              break;
                            case "toolSelection":
                              if (
                                ["Inline_FL_G", "Inline_FL_O"].includes(
                                  configuration.gearbox.key,
                                )
                              ) {
                                setPercentComplete(0.6);
                                setCurrentPage("lsp3Connect");
                              } else {
                                setPercentComplete(0.7);
                                setCurrentPage("angleHeadSelection");
                              }
                              break;
                            case "serviceSelection":
                              setPercentComplete(0.9);
                              setCurrentPage("toolSelection");
                              break;
                          }
                        }}
                      >
                        {i18n.t<string>("back")}
                      </Button>
                    )}
                  {currentPage != "lsp3Result" && (
                    <Button
                      style={{ marginTop: "5px" }}
                      iconPosition="after"
                      icon={<ArrowRightRegular />}
                      appearance="primary"
                      disabled={isModelDisabled()}
                      onClick={() => {
                        switch (currentPage) {
                          case "schraubfall":
                            setCurrentPage("model");
                            setPercentComplete(0.2);
                            break;
                          case "model":
                            // TODO: Unterscheiden nach ausgewähltem Model -> Rohrleitung anders wie L.SP3 Access
                            if (
                              [
                                "Standard_FL_G",
                                "Inline_FL_G",
                                "Standard_FL_O",
                                "Inline_FL_O",
                                "Winkelkopf",
                              ].includes(configuration.gearbox.key)
                            ) {
                              setCurrentPage("headSpecifications");
                              setPercentComplete(0.3);
                            } else if (
                              configuration.gearbox.key == "Axial_Abtrieb"
                            ) {
                              setCurrentPage("lsp3Selection");
                              setPercentComplete(0.5);
                            }
                            break;
                          case "headSpecifications":
                            if (
                              [
                                "Standard_FL_G",
                                "Inline_FL_G",
                                "Standard_FL_O",
                                "Inline_FL_O",
                              ].includes(configuration.gearbox.key)
                            ) {
                              setCurrentPage("lsp3touch");
                              setPercentComplete(0.4);
                            } else {
                              setCurrentPage("lsp3Selection");
                              setPercentComplete(0.5);
                            }
                            break;
                          case "lsp3touch":
                            setCurrentPage("lsp3Selection");
                            setPercentComplete(0.5);
                            break;
                          case "lsp3Selection":
                            // Meldung über ausgewähltes Antriebsset einblenden, in diesem wird dann das Pivot weitergeschaltet
                            if (configuration.gearbox.key != "Winkelkopf") {
                              openDriveSetSelectedDialog();
                            } else {
                              if (
                                ["Standard_FL_G", "Standard_FL_O"].includes(
                                  configuration.gearbox.key,
                                )
                              ) {
                                // Wenn LSP3 Standard, Connect Pivot anzeigen
                                setCurrentPage("lsp3Connect");
                              } else {
                                // Sonst direkt zur Toolauswahl
                                setCurrentPage("toolSelection");
                              }
                            }
                            setPercentComplete(0.6);
                            break;
                          case "lsp3Connect":
                            // Der Dialog "Auswahl Winkelkopf" wird bei Auswahl eines Inline-Flachabtriebs übersprungen.
                            if (
                              ["Inline_FL_G", "Inline_FL_O"].includes(
                                configuration.gearbox.key,
                              )
                            ) {
                              setCurrentPage("toolSelection");
                              setPercentComplete(0.9);
                            } else {
                              setCurrentPage("angleHeadSelection");
                              setPercentComplete(0.7);
                            }
                            break;
                          case "angleHeadSelection":
                            setCurrentPage("toolSelection");
                            setPercentComplete(0.8);
                            break;
                          case "toolSelection":
                            if (
                              configuration.gearbox.key == "Winkelkopf" ||
                              configuration?.selectedAngleHead
                                ?.selectedAngleHead ||
                              ["Inline_FL_G", "Inline_FL_O"].includes(
                                configuration.gearbox.key,
                              )
                            ) {
                              getAxialAdapter();
                              openAxialAdapterSelectedDialog();
                            } else {
                              setCurrentPage("serviceSelection");
                            }
                            setPercentComplete(0.9);
                            break;
                          case "serviceSelection":
                            setCurrentPage("lsp3Result");
                            setPercentComplete(1);
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      {i18n.t<string>("next")}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
          {currentPage == "lsp3Selection" && (
            <Lsp3Selection
              isButtonDisabled={isModelDisabled()}
              configurationParameter={configuration}
              onSelectionChanged={(data: any) => {
                handleConfigurationValuesChange("lsp3Selection", data);
              }}
              preiseSichtbar={preiseSichtbar}
              onChangePage={(item: string) => {
                setCurrentPage(item);
              }}
              onOpenDialog={() => openDriveSetSelectedDialog()}
              onChangePercent={(item: number) => {
                setPercentComplete(item);
              }}
            />
          )}

          {currentPage == "lsp3Result" && (
            <ConfiguratorStResult
              finishedConfig={finishedConfig}
              configurationParameter={configuration}
              onConfigurationFinished={(config: any) => {
                setFinishedConfig(config);
              }}
              preiseSichtbar={preiseSichtbar}
              isVertrieb={isVertrieb}
              onFinishConfig={() => {
                finishConfig();
              }}
              onFinishedConfigChange={(item) => {
                setFinishedConfig(item);
              }}
              onDismiss={() => {
                props.onDismiss();
                // Zurücksetzen der Konfig mit Timeout damit der Rendering Effekt schöner ist!
                setTimeout(() => {
                  setPercentComplete(0);
                  setCurrentPage("schraubfall");
                  setConfiguration(_blankConfig);
                  setFinishedConfig(undefined);
                }, 500);
              }}
              onBackButtonClick={() => {
                setPercentComplete(0.8);
                setCurrentPage("serviceSelection");
                configRestarted();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

export default ConfiguratorStPanel;
